<template>
  <SpmOverlayPanel
    class-trigger="flex text-center"
    class-header="flex align-items-center p-3 justify-content-left"
    class-content="flex flex-column justify-content-center spm-overlay-panel-content py-3 px-3"
    class-footer="flex align-items-center justify-content-center spm-overlay-panel-content"
    min-panel-width="380px"
    max-panel-width="380px"
    :close-panel="closePanel"
    @onclose-panel="closePanel = false"
  >
    <template #trigger>
      <SpmButton
        :label="t('myLists.manage.actions.listCreate')"
        icon="fa fa-plus"
        class-style="p-button p-button-success"
      />
    </template>

    <template #header>
      <div class="flex align-items-center justify-content-center ml-2">
        <h4>{{ t('myLists.manage.createPanelV2.title') }}</h4>
      </div>
    </template>

    <!-- List name input -->
    <div class="field mb-3">
      <BaseTextInput
        v-model="listName"
        input-type="text"
        field-id="listName"
        label="myLists.manage.nameList"
        input-style="width: 100%;"
        :errors="formValidation"
      />
    </div>

    <!-- Type selection -->
    <div class="field mb-3">
      <label class="block field-label mb-2">
        {{ t('myLists.manage.createPanelV2.typeLabel') }}
      </label>
      <div class="type-selector">
        <div
          class="type-option"
          :class="{ 'type-option--selected': selectedType === 'list' }"
          @click="selectedType = 'list'"
        >
          <div class="type-option__icon">
            <i class="fa fa-list" />
          </div>
          <div class="type-option__content">
            <span class="type-option__title">{{ t('myLists.manage.createPanelV2.types.list.title') }}</span>
            <span class="type-option__desc">{{ t('myLists.manage.createPanelV2.types.list.description') }}</span>
          </div>
          <div
            v-if="selectedType === 'list'"
            class="type-option__check"
          >
            <i class="fa fa-check-circle" />
          </div>
        </div>

        <div
          class="type-option"
          :class="{ 'type-option--selected': selectedType === 'segment' }"
          @click="selectedType = 'segment'"
        >
          <div class="type-option__icon">
            <i class="fa fa-filter" />
          </div>
          <div class="type-option__content">
            <span class="type-option__title">{{ t('myLists.manage.createPanelV2.types.segment.title') }}</span>
            <span class="type-option__desc">{{ t('myLists.manage.createPanelV2.types.segment.description') }}</span>
          </div>
          <div
            v-if="selectedType === 'segment'"
            class="type-option__check"
          >
            <i class="fa fa-check-circle" />
          </div>
        </div>
      </div>
    </div>

    <!-- Import checkbox (only for list type) -->
    <div
      v-if="selectedType === 'list'"
      class="field mb-2"
    >
      <div class="import-checkbox">
        <Checkbox
          v-model="importAfterCreate"
          input-id="importAfterCreate"
          :binary="true"
        />
        <label
          for="importAfterCreate"
          class="ml-2 cursor-pointer"
        >
          {{ t('myLists.manage.createPanelV2.importCheckbox') }}
        </label>
      </div>
    </div>

    <template #footer>
      <SpmButton
        :label="t('myLists.manage.createPanelV2.createButton')"
        :loading="loading"
        class-style="p-button p-button-success"
        @click="handleCreate"
      />
    </template>
  </SpmOverlayPanel>
</template>

<script lang="ts">
import {
  defineComponent, reactive, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import SpmOverlayPanel from '@/components/spm-primevue/SpmOverlayPanel.vue';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import BaseTextInput from '@/components/fields/BaseTextInput.vue';
import Checkbox from 'primevue/checkbox';
import { required } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { ListType } from '@/types';
import { showToastError, showToastSuccess } from '@/helpers';
import { nestCall, NestApiType } from '@/composables/nestApi';

export default defineComponent({
  name: 'ListCreatePanelV2',

  components: {
    SpmOverlayPanel,
    SpmButton,
    BaseTextInput,
    Checkbox,
  },

  setup() {
    const { t } = useI18n();
    const router = useRouter();

    const loading = ref(false);
    const closePanel = ref(false);
    const formValidation: any = ref<Record<string, unknown>>({});

    // Form state
    const listName = ref('');
    const selectedType = ref<'list' | 'segment'>('list');
    const importAfterCreate = ref(true);

    const handleCreate = async () => {
      // Validate form
      const data = reactive({
        listName: listName.value,
      });

      const rules = {
        listName: { required },
      };

      const v$ = useVuelidate(rules, data);
      const success = await v$.value.$validate();

      if (!success) {
        formValidation.value = v$;
        return;
      }

      loading.value = true;

      try {
        // Create the list via NestJS API
        const payload = {
          name: listName.value,
          type: selectedType.value === 'segment' ? ListType.SEGMENT : ListType.LIST,
        };

        const response = await nestCall(
          'post',
          NestApiType.CLIENT_API,
          '/lists',
          {},
          '',
          payload,
          true,
        );

        if (!response || response.statusCode) {
          const errorMessage = response?.message || t('myLists.manage.createPanelV2.error');
          await showToastError(errorMessage);
          loading.value = false;
          return;
        }

        // Get the new list ID from response
        const newListId = response.id_shop_list;

        // Save values before reset
        const shouldImport = selectedType.value === 'list' && importAfterCreate.value;

        await showToastSuccess(t('myLists.manage.createPanelV2.success'));

        // Close panel
        closePanel.value = true;

        // Reset form
        listName.value = '';
        selectedType.value = 'list';
        importAfterCreate.value = true;
        formValidation.value = {};

        // Redirect to contacts management page with optional import query param
        await router.push({
          name: 'lists.manage-contacts',
          params: { id: newListId },
          query: shouldImport ? { openImport: 'true' } : undefined,
        });
      } catch (error) {
        console.error('Error creating list:', error);
        await showToastError(t('myLists.manage.createPanelV2.error'));
      } finally {
        loading.value = false;
      }
    };

    return {
      t,
      loading,
      closePanel,
      formValidation,
      listName,
      selectedType,
      importAfterCreate,
      handleCreate,
    };
  },
});
</script>

<style scoped lang="scss">
.type-selector {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
}

.type-option {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border: 2px solid var(--surface-border);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--primary-color);
    background-color: var(--surface-hover);
  }

  &--selected {
    border-color: var(--primary-color);
    background-color: var(--primary-50, rgba(var(--primary-color-rgb), 0.1));
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background-color: var(--surface-100);
    color: var(--text-color-secondary);

    i {
      font-size: 1.25rem;
    }
  }

  &--selected &__icon {
    background-color: var(--primary-color);
    color: white;
  }

  &__content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__title {
    font-weight: 600;
    color: var(--text-color);
  }

  &__desc {
    font-size: 0.85rem;
    color: var(--text-color-secondary);
  }

  &__check {
    color: var(--primary-color);
    font-size: 1.25rem;
  }
}

.import-checkbox {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  background-color: var(--surface-50);
  border-radius: 6px;

  label {
    font-size: 0.9rem;
    color: var(--text-color);
  }
}
</style>
